import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, X, FileText, Eye } from 'lucide-react'
import { blogService } from '@/services/blogService'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { imgUrl } from '@/utils/img'
import { formatDate } from '@/utils/formatters'

const localized = (v, lang) => (typeof v === 'object' && v ? (v[lang] || v.ar || v.en || '') : (v || ''))

const EMPTY = {
  id: null,
  slug: '',
  titleAr: '', titleEn: '',
  excerptAr: '', excerptEn: '',
  contentAr: '', contentEn: '',
  coverImage: '',
  tags: '',
  status: 'draft',
  authorName: '',
  metaTitleAr: '', metaTitleEn: '',
  metaDescAr: '', metaDescEn: '',
}

function toForm(post) {
  if (!post) return { ...EMPTY }
  return {
    id: post.id,
    slug: post.slug || '',
    titleAr: post.title?.ar || '', titleEn: post.title?.en || '',
    excerptAr: post.excerpt?.ar || '', excerptEn: post.excerpt?.en || '',
    contentAr: post.content?.ar || '', contentEn: post.content?.en || '',
    coverImage: post.coverImage || '',
    tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
    status: post.status || 'draft',
    authorName: post.authorName || '',
    metaTitleAr: post.metaTitle?.ar || '', metaTitleEn: post.metaTitle?.en || '',
    metaDescAr: post.metaDescription?.ar || '', metaDescEn: post.metaDescription?.en || '',
  }
}

function toPayload(f) {
  const tags = f.tags.split(',').map((t) => t.trim()).filter(Boolean)
  const payload = {
    slug: f.slug.trim(),
    title: { ar: f.titleAr.trim(), en: f.titleEn.trim() },
    excerpt: { ar: f.excerptAr.trim(), en: f.excerptEn.trim() },
    content: { ar: f.contentAr, en: f.contentEn },
    coverImage: f.coverImage.trim() || null,
    tags,
    status: f.status,
    authorName: f.authorName.trim() || undefined,
  }
  const metaTitle = { ar: f.metaTitleAr.trim(), en: f.metaTitleEn.trim() }
  const metaDescription = { ar: f.metaDescAr.trim(), en: f.metaDescEn.trim() }
  if (metaTitle.ar || metaTitle.en) payload.metaTitle = metaTitle
  if (metaDescription.ar || metaDescription.en) payload.metaDescription = metaDescription
  return payload
}

// Auto-slug helper for the create flow
const slugify = (s) => s.toLowerCase().trim().replace(/[^\w؀-ۿ]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 200)

export default function BlogPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc = useQueryClient()

  const [statusFilter, setStatusFilter] = useState('')
  const { page, limit, setPage } = usePagination(1, 12)
  const [dialog, setDialog] = useState({ open: false, form: null })
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['blog', { statusFilter, page, limit }],
    queryFn: () => blogService.getAll({ status: statusFilter || undefined, page, limit }),
  })

  const posts = data?.data || []
  const meta = data?.meta || {}

  const invalidate = () => qc.invalidateQueries({ queryKey: ['blog'] })

  const saveMut = useMutation({
    mutationFn: (form) => (form.id ? blogService.update(form.id, toPayload(form)) : blogService.create(toPayload(form))),
    onSuccess: () => { toast.success('تم الحفظ'); setDialog({ open: false, form: null }); invalidate() },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل الحفظ'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => blogService.remove(id),
    onSuccess: () => { toast.success('تم حذف المقال'); setDeleteTarget(null); invalidate() },
    onError: () => toast.error('فشل الحذف'),
  })

  const openEdit = async (post) => {
    setDialog({ open: true, form: toForm(post) }) // list has everything we need
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 className="font-black text-navy text-xl" style={{ fontFamily:'Cairo, sans-serif' }}>{t('nav.blog')}</h1>
          <p className="text-sm text-ghost mt-1" style={{ fontFamily:'Tajawal, sans-serif' }}>إدارة مقالات المدونة (SEO)</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }} className="hg-input" style={{ width:150 }}>
            <option value="">كل الحالات</option>
            <option value="published">منشور</option>
            <option value="draft">مسودّة</option>
          </select>
          <button onClick={() => setDialog({ open: true, form: { ...EMPTY } })} className="hg-btn-navy px-5 py-2 text-sm" style={{ display:'flex', alignItems:'center', gap:6 }}>
            <Plus size={16} />مقال جديد
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="hg-card p-8 flex flex-col items-center gap-4">
          <p style={{ color:'#BA1A1A', fontWeight:700, fontFamily:'Cairo, sans-serif' }}>فشل تحميل البيانات</p>
          <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
        </div>
      ) : posts.length === 0 ? (
        <div className="hg-card"><EmptyState icon={FileText} message="لا توجد مقالات بعد" /></div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:16 }}>
            {posts.map((post) => (
              <div key={post.id} className="hg-card" style={{ overflow:'hidden', display:'flex', flexDirection:'column' }}>
                <div style={{ height:130, background:'#F0F0F5', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {post.coverImage
                    ? <img src={imgUrl(post.coverImage)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={(e) => { e.target.style.display='none' }} />
                    : <FileText size={28} style={{ color:'#C4C4C4' }} />}
                </div>
                <div style={{ padding:14, display:'flex', flexDirection:'column', gap:6, flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:10, fontWeight:700, fontFamily:'Cairo, sans-serif', padding:'2px 8px', borderRadius:6,
                      background: post.status === 'published' ? 'rgba(27,109,36,0.1)' : 'rgba(118,118,131,0.12)',
                      color: post.status === 'published' ? '#1B6D24' : '#767683' }}>
                      {post.status === 'published' ? 'منشور' : 'مسودّة'}
                    </span>
                    <span style={{ fontSize:11, color:'#767683', fontFamily:'Tajawal, sans-serif', display:'flex', alignItems:'center', gap:3 }}>
                      <Eye size={12} />{post.viewsCount ?? 0}
                    </span>
                  </div>
                  <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#1A1C1C' }}>{localized(post.title, lang) || '—'}</div>
                  <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:12, color:'#767683', direction:'ltr', textAlign:'start' }}>/{post.slug}</div>
                  <div style={{ fontSize:11, color:'#9A9AA5', fontFamily:'Tajawal, sans-serif', marginTop:'auto' }}>
                    {post.publishedAt ? formatDate(post.publishedAt) : formatDate(post.createdAt)}
                  </div>
                  <div style={{ display:'flex', gap:8, borderTop:'1px solid #F3F3F3', paddingTop:10, marginTop:4 }}>
                    <button onClick={() => openEdit(post)} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'7px 0', borderRadius:9, fontSize:12, fontWeight:700, fontFamily:'Cairo, sans-serif', background:'rgba(0,6,102,0.06)', color:'#000666', border:'none', cursor:'pointer' }}>
                      <Pencil size={13} />تعديل
                    </button>
                    <button onClick={() => setDeleteTarget(post)} style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'7px 12px', borderRadius:9, background:'rgba(186,26,26,0.08)', color:'#BA1A1A', border:'none', cursor:'pointer' }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="hg-card p-4">
            <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
          </div>
        </>
      )}

      {dialog.open && (
        <BlogDialog
          initial={dialog.form}
          saving={saveMut.isPending}
          onClose={() => setDialog({ open: false, form: null })}
          onSave={(form) => saveMut.mutate(form)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف المقال"
        message={`هل تريد حذف "${localized(deleteTarget?.title, lang)}"؟ لا يمكن التراجع.`}
        onConfirm={() => deleteMut.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#454652', fontFamily:'Cairo, sans-serif', marginBottom:5 }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize:11, color:'#9A9AA5', fontFamily:'Tajawal, sans-serif', marginTop:4 }}>{hint}</p>}
    </div>
  )
}

function BlogDialog({ initial, onClose, onSave, saving }) {
  const [f, setF] = useState(initial)
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }))

  const canSave = f.slug.trim() && f.titleAr.trim() && f.titleEn.trim() && !saving

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <div onClick={(e) => e.stopPropagation()} dir="rtl" style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:640, maxHeight:'92vh', overflowY:'auto', padding:22 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:16, color:'#000666' }}>{f.id ? 'تعديل المقال' : 'مقال جديد'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#767683' }}><X size={20} /></button>
        </div>

        <Field label="الرابط (slug)" hint="يظهر في عنوان الصفحة: /blog/your-slug — بدون مسافات">
          <input value={f.slug} onChange={(e) => set('slug', e.target.value)} onBlur={(e) => { if (e.target.value) set('slug', slugify(e.target.value)) }}
            placeholder="how-to-choose-a-product" className="hg-input w-full" style={{ direction:'ltr', textAlign:'left' }} />
        </Field>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="العنوان (عربي)"><input value={f.titleAr} onChange={(e) => set('titleAr', e.target.value)} className="hg-input w-full" /></Field>
          <Field label="العنوان (إنجليزي)"><input value={f.titleEn} onChange={(e) => set('titleEn', e.target.value)} className="hg-input w-full" style={{ direction:'ltr', textAlign:'left' }} /></Field>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="المقتطف (عربي)"><textarea value={f.excerptAr} onChange={(e) => set('excerptAr', e.target.value)} rows={2} className="hg-input w-full" style={{ resize:'vertical' }} /></Field>
          <Field label="المقتطف (إنجليزي)"><textarea value={f.excerptEn} onChange={(e) => set('excerptEn', e.target.value)} rows={2} className="hg-input w-full" style={{ direction:'ltr', textAlign:'left', resize:'vertical' }} /></Field>
        </div>

        <Field label="المحتوى (عربي) — يدعم HTML"><textarea value={f.contentAr} onChange={(e) => set('contentAr', e.target.value)} rows={5} className="hg-input w-full" style={{ resize:'vertical' }} /></Field>
        <Field label="المحتوى (إنجليزي) — يدعم HTML"><textarea value={f.contentEn} onChange={(e) => set('contentEn', e.target.value)} rows={5} className="hg-input w-full" style={{ direction:'ltr', textAlign:'left', resize:'vertical' }} /></Field>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="رابط صورة الغلاف"><input value={f.coverImage} onChange={(e) => set('coverImage', e.target.value)} placeholder="/uploads/... أو https://..." className="hg-input w-full" style={{ direction:'ltr', textAlign:'left' }} /></Field>
          <Field label="اسم الكاتب"><input value={f.authorName} onChange={(e) => set('authorName', e.target.value)} className="hg-input w-full" /></Field>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="الوسوم (مفصولة بفاصلة)"><input value={f.tags} onChange={(e) => set('tags', e.target.value)} placeholder="تسوق, نصائح" className="hg-input w-full" /></Field>
          <Field label="الحالة">
            <select value={f.status} onChange={(e) => set('status', e.target.value)} className="hg-input w-full">
              <option value="draft">مسودّة</option>
              <option value="published">منشور</option>
            </select>
          </Field>
        </div>

        {/* SEO */}
        <div style={{ borderTop:'1px solid #F0F0F0', marginTop:8, paddingTop:14 }}>
          <div style={{ fontSize:12, fontWeight:800, color:'#000666', fontFamily:'Cairo, sans-serif', marginBottom:10 }}>تحسين محركات البحث (SEO) — اختياري</div>
          <p style={{ fontSize:11, color:'#9A9AA5', fontFamily:'Tajawal, sans-serif', marginBottom:12, marginTop:-4 }}>لو تركته فاضي، بيتحسب من العنوان والمقتطف تلقائياً.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Meta Title (عربي)"><input value={f.metaTitleAr} onChange={(e) => set('metaTitleAr', e.target.value)} className="hg-input w-full" /></Field>
            <Field label="Meta Title (إنجليزي)"><input value={f.metaTitleEn} onChange={(e) => set('metaTitleEn', e.target.value)} className="hg-input w-full" style={{ direction:'ltr', textAlign:'left' }} /></Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Meta Description (عربي)"><textarea value={f.metaDescAr} onChange={(e) => set('metaDescAr', e.target.value)} rows={2} className="hg-input w-full" style={{ resize:'vertical' }} /></Field>
            <Field label="Meta Description (إنجليزي)"><textarea value={f.metaDescEn} onChange={(e) => set('metaDescEn', e.target.value)} rows={2} className="hg-input w-full" style={{ direction:'ltr', textAlign:'left', resize:'vertical' }} /></Field>
          </div>
        </div>

        <div style={{ display:'flex', gap:10, marginTop:6 }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px 0', borderRadius:10, border:'1px solid #E8E8E8', background:'#fff', color:'#454652', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, cursor:'pointer' }}>إلغاء</button>
          <button onClick={() => canSave && onSave(f)} disabled={!canSave} className="hg-btn-navy" style={{ flex:1, padding:'10px 0', fontSize:13, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
            {saving ? 'جارٍ الحفظ…' : 'حفظ'}
          </button>
        </div>
      </div>
    </div>
  )
}
